/**
 * InsureJewellery Component
 * Displays jewellery insurance and loan options on listing detail page
 * Similar to FinanceThisCar and InsureThisCar for vehicles
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useCountry } from '../context/CountryContext';
import {
  Shield,
  Banknote,
  Gem,
  Star,
  CheckCircle2,
  Percent,
  Loader2,
  Phone,
  Mail,
  Building2,
  FileCheck,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Insurance Quote Dialog
const InsuranceQuoteDialog = ({ open, onOpenChange, listing, providers }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [formData, setFormData] = useState({
    item_type: '',
    item_value: listing?.price || '',
    description: listing?.title || '',
    has_certification: false,
    certification_type: '',
    coverage_type: 'comprehensive',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/jewellery-services/insurance/quote`, {
        ...formData,
        item_value: Number(formData.item_value),
        listing_id: listing?.id,
      });
      if (response.data.success) {
        setQuoteId(response.data.quote_id);
        setSubmitted(true);
      }
    } catch (err) {
      alert('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your reference number is: <span className="font-bold text-blue-600">{quoteId}</span>
            </p>
            <p className="text-sm text-gray-500">
              Our insurance partners will contact you within 24 hours with quotes for your jewellery.
            </p>
            <Button className="mt-6" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Get Insurance Quote
          </DialogTitle>
          <DialogDescription>
            Protect your precious jewellery with comprehensive coverage
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Item Type *</Label>
              <Select value={formData.item_type} onValueChange={(v) => setFormData({...formData, item_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ring">Ring</SelectItem>
                  <SelectItem value="necklace">Necklace</SelectItem>
                  <SelectItem value="bracelet">Bracelet</SelectItem>
                  <SelectItem value="earrings">Earrings</SelectItem>
                  <SelectItem value="watch">Watch</SelectItem>
                  <SelectItem value="pendant">Pendant</SelectItem>
                  <SelectItem value="chain">Chain</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Item Value (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 500000"
                value={formData.item_value}
                onChange={(e) => setFormData({...formData, item_value: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Item Description *</Label>
            <Textarea
              placeholder="Describe your jewellery (material, gems, weight, etc.)"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Coverage Type</Label>
            <Select value={formData.coverage_type} onValueChange={(v) => setFormData({...formData, coverage_type: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comprehensive">Comprehensive (Theft + Damage + Loss)</SelectItem>
                <SelectItem value="theft_only">Theft Only</SelectItem>
                <SelectItem value="damage_only">Damage Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                placeholder="07X XXX XXXX"
                value={formData.customer_phone}
                onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email (Optional)</Label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.customer_email}
              onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
            />
          </div>

          <Button
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={handleSubmit}
            disabled={loading || !formData.item_type || !formData.item_value || !formData.customer_name || !formData.customer_phone}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
            Get Insurance Quotes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Loan Application Dialog
const LoanApplicationDialog = ({ open, onOpenChange, listing, providers }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [formData, setFormData] = useState({
    item_type: '',
    item_description: listing?.title || '',
    estimated_value: listing?.price || '',
    loan_amount_requested: '',
    loan_term_months: 12,
    customer_name: '',
    customer_phone: '',
    customer_nic: '',
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/jewellery-services/loans/apply`, {
        ...formData,
        estimated_value: Number(formData.estimated_value),
        loan_amount_requested: Number(formData.loan_amount_requested),
        listing_id: listing?.id,
      });
      if (response.data.success) {
        setApplicationId(response.data.application_id);
        setSubmitted(true);
      }
    } catch (err) {
      alert('Failed to submit loan application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate LTV
  const ltv = formData.estimated_value && formData.loan_amount_requested
    ? ((Number(formData.loan_amount_requested) / Number(formData.estimated_value)) * 100).toFixed(1)
    : 0;

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your reference number is: <span className="font-bold text-green-600">{applicationId}</span>
            </p>
            <p className="text-sm text-gray-500">
              Our loan partners will contact you within 24 hours to discuss your gold loan options.
            </p>
            <Button className="mt-6" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-green-500" />
            Apply for Gold / Jewellery Loan
          </DialogTitle>
          <DialogDescription>
            Get instant cash against your gold and jewellery
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Item Type *</Label>
              <Select value={formData.item_type} onValueChange={(v) => setFormData({...formData, item_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gold_jewellery">Gold Jewellery</SelectItem>
                  <SelectItem value="gold_coins">Gold Coins</SelectItem>
                  <SelectItem value="diamond_jewellery">Diamond Jewellery</SelectItem>
                  <SelectItem value="silver_jewellery">Silver Jewellery</SelectItem>
                  <SelectItem value="watch">Luxury Watch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estimated Value (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 500000"
                value={formData.estimated_value}
                onChange={(e) => setFormData({...formData, estimated_value: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Item Description *</Label>
            <Textarea
              placeholder="Describe your jewellery (weight in grams, karats, etc.)"
              value={formData.item_description}
              onChange={(e) => setFormData({...formData, item_description: e.target.value})}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Loan Amount Required (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 300000"
                value={formData.loan_amount_requested}
                onChange={(e) => setFormData({...formData, loan_amount_requested: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Loan Term</Label>
              <Select value={String(formData.loan_term_months)} onValueChange={(v) => setFormData({...formData, loan_term_months: Number(v)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* LTV Indicator */}
          {formData.estimated_value && formData.loan_amount_requested && (
            <div className={`p-3 rounded-lg ${Number(ltv) <= 75 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Loan-to-Value Ratio:</span>
                <Badge className={Number(ltv) <= 75 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {ltv}%
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {Number(ltv) <= 75 ? '✓ Within acceptable range (max 75%)' : '⚠ May require additional collateral'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>NIC Number *</Label>
              <Input
                placeholder="National ID"
                value={formData.customer_nic}
                onChange={(e) => setFormData({...formData, customer_nic: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Phone Number *</Label>
            <Input
              placeholder="07X XXX XXXX"
              value={formData.customer_phone}
              onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
            />
          </div>

          <Button
            className="w-full bg-green-600 hover:bg-green-700"
            onClick={handleSubmit}
            disabled={loading || !formData.item_type || !formData.estimated_value || !formData.loan_amount_requested || !formData.customer_name || !formData.customer_nic || !formData.customer_phone}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
            Submit Loan Application
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Provider Card Component
const ProviderCard = ({ provider, type, onSelect }) => {
  const isInsurance = type === 'insurance';
  const isLoan = type === 'loan';

  return (
    <div className="p-4 border rounded-lg hover:border-blue-300 hover:shadow-sm transition-all bg-white">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900">{provider.provider_name}</h4>
            {provider.is_featured && (
              <Badge className="bg-yellow-100 text-yellow-700 text-xs">Featured</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500">{provider.business_name}</p>
          
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-medium">{provider.rating?.toFixed(1)}</span>
            </div>
            
            {isInsurance && (
              <Badge variant="outline" className="text-xs">
                {provider.premium_rate}% p.a.
              </Badge>
            )}
            
            {isLoan && (
              <>
                <Badge variant="outline" className="text-xs text-green-700 border-green-300">
                  {provider.interest_rate}% p.a.
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Up to {provider.max_ltv}% LTV
                </Badge>
              </>
            )}
          </div>

          {provider.features && provider.features.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {provider.features.slice(0, 3).map((f, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  <CheckCircle2 className="h-2 w-2 mr-1" />
                  {f}
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => onSelect(provider)}
          className="ml-2"
        >
          Select
          <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Main Component
const InsureJewellery = ({ listing }) => {
  const { country } = useCountry();
  const [settings, setSettings] = useState(null);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [loanProviders, setLoanProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false);
  const [showLoanDialog, setShowLoanDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [country]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, insuranceRes, loansRes] = await Promise.all([
        axios.get(`${API_URL}/api/jewellery-services/admin/settings/${country}`),
        axios.get(`${API_URL}/api/jewellery-services/insurance/providers?country=${country}`),
        axios.get(`${API_URL}/api/jewellery-services/loans/providers?country=${country}`),
      ]);
      setSettings(settingsRes.data);
      setInsuranceProviders(insuranceRes.data.providers || []);
      setLoanProviders(loansRes.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch jewellery services:', err);
    } finally {
      setLoading(false);
    }
  };

  const showInsurance = settings?.insurance_enabled !== false && insuranceProviders.length > 0;
  const showLoans = settings?.loans_enabled !== false && loanProviders.length > 0;

  if (loading) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-yellow-500" />
        </CardContent>
      </Card>
    );
  }

  if (!showInsurance && !showLoans) {
    return null;
  }

  return (
    <>
      {/* Insurance Accordion */}
      {showInsurance && (
        <Card className="mt-4 border-blue-200" data-testid="insure-jewellery-section">
          <Accordion type="single" collapsible defaultValue="insurance">
            <AccordionItem value="insurance" className="border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-900">Insure this Jewellery</h3>
                    <p className="text-sm text-gray-500">Protect against theft, loss & damage</p>
                  </div>
                  <Badge className="ml-auto bg-blue-100 text-blue-700">
                    {insuranceProviders.length} providers
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3">
                  {insuranceProviders.slice(0, 3).map((provider) => (
                    <ProviderCard
                      key={provider.id}
                      provider={provider}
                      type="insurance"
                      onSelect={() => setShowInsuranceDialog(true)}
                    />
                  ))}
                  
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 mt-4"
                    onClick={() => setShowInsuranceDialog(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Get Insurance Quote
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      )}

      {/* Loans Accordion */}
      {showLoans && (
        <Card className="mt-4 border-green-200" data-testid="loan-jewellery-section">
          <Accordion type="single" collapsible defaultValue="loans">
            <AccordionItem value="loans" className="border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                    <Banknote className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-900">Get a Gold Loan</h3>
                    <p className="text-sm text-gray-500">Instant cash against your jewellery</p>
                  </div>
                  <Badge className="ml-auto bg-green-100 text-green-700">
                    From {Math.min(...loanProviders.map(p => p.interest_rate))}% p.a.
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-3">
                  {loanProviders.slice(0, 3).map((provider) => (
                    <ProviderCard
                      key={provider.id}
                      provider={provider}
                      type="loan"
                      onSelect={() => setShowLoanDialog(true)}
                    />
                  ))}
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 mt-4"
                    onClick={() => setShowLoanDialog(true)}
                  >
                    <Banknote className="h-4 w-4 mr-2" />
                    Apply for Gold Loan
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      )}

      {/* Dialogs */}
      <InsuranceQuoteDialog
        open={showInsuranceDialog}
        onOpenChange={setShowInsuranceDialog}
        listing={listing}
        providers={insuranceProviders}
      />
      
      <LoanApplicationDialog
        open={showLoanDialog}
        onOpenChange={setShowLoanDialog}
        listing={listing}
        providers={loanProviders}
      />
    </>
  );
};

export default InsureJewellery;
